import React, { useEffect, useMemo, useState } from "react";
import {
  fetchAdminJson,
  fetchAllPages,
  buildHierarchyRows,
  roleLabel,
  roleBadgeStyle,
  ROLE_OPTIONS,
  HIERARCHY_CITY_REGION,
  normalizeCityForHierarchy,
} from "../settingsHelpers";

export function HierarchyTab() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [geographies, setGeographies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError("");
      const [allUsers, allTeams, allGeographies] = await Promise.all([
        fetchAllPages("/api/accounts/users/"),
        fetchAllPages("/api/accounts/teams/"),
        fetchAllPages("/api/crm/geographies/"),
      ]);
      setUsers(allUsers);
      setTeams(allTeams);
      setGeographies(allGeographies);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHierarchy();
  }, []);

  const hierarchyUsers = useMemo(
    () => users.filter((user) => user.role !== "admin"),
    [users],
  );

  const editableManagers = useMemo(
    () => users.filter((user) => user.role === "manager" || user.role === "senior_manager"),
    [users],
  );

  const hierarchyRows = useMemo(
    () => buildHierarchyRows(hierarchyUsers, teams),
    [hierarchyUsers, teams],
  );

  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const expandAll = () => {
    const next = {};
    hierarchyRows.forEach((region) => {
      next[`r-${region.id}`] = true;
      region.cities.forEach((city) => {
        next[`c-${city.id}`] = true;
        city.managers.forEach((mgr) => {
          next[`m-${mgr.id}`] = true;
        });
      });
    });
    setExpanded(next);
  };

  const collapseAll = () => setExpanded({});

  const startEdit = (user) => {
    setEditingId(String(user.id));
    const u = hierarchyUsers.find((h) => String(h.id) === String(user.id)) || user;
    const assignedCities = (u.geography_scopes || [])
      .map((s) => normalizeCityForHierarchy(s.city_name || ""))
      .filter(Boolean);
    setEditForm({
      role: u.role || user.role,
      team: u.team ? String(u.team) : "",
      cityNames: assignedCities,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveUser = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await fetchAdminJson(`/api/accounts/users/${editingId}/`, {
        method: "PATCH",
        body: JSON.stringify({
          role: editForm.role,
          team: editForm.team || null,
          city_names: editForm.cityNames || [],
        }),
      });
      setNotice("User updated.");
      setEditingId(null);
      await loadHierarchy();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTeamManager = async (teamId, managerId) => {
    try {
      setSaving(true);
      setError("");
      setNotice("");
      await fetchAdminJson(`/api/accounts/teams/${teamId}/`, {
        method: "PATCH",
        body: JSON.stringify({ manager: managerId || null }),
      });
      setNotice("Team manager updated.");
      await loadHierarchy();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const indent = (level) => ({ paddingLeft: `${level * 1.5 + 0.75}rem` });

  const renderTreeRow = (label, badge, badgeStyle, level, expandKey, childCount, person) => {
    const isExpanded = expanded[expandKey];
    const isEditing = person && String(editingId) === String(person.id);

    return (
      <div
        key={expandKey}
        style={{
          alignItems: "center",
          background: isEditing ? "#eef4ff" : "transparent",
          borderBottom: "1px solid var(--border)",
          display: "grid",
          gap: "0.5rem",
          gridTemplateColumns: "1fr auto",
          minHeight: "44px",
          padding: "0.5rem 0.75rem",
          ...indent(level),
        }}
      >
        <div style={{ alignItems: "center", display: "flex", gap: "0.5rem" }}>
          {childCount > 0 ? (
            <button
              type="button"
              onClick={() => toggle(expandKey)}
              style={{
                background: "none",
                border: "none",
                color: "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "12px",
                padding: "2px 4px",
                width: "20px",
              }}
            >
              {isExpanded ? "▼" : "▶"}
            </button>
          ) : (
            <span style={{ display: "inline-block", width: "20px" }} />
          )}
          <span style={{ fontWeight: person ? 400 : 600 }}>{label}</span>
          {person?.otherCityName ? (
            <span style={{ color: "var(--text-tertiary)", fontSize: "12px", fontStyle: "italic" }}>
              {`{${person.otherCityName}}`}
            </span>
          ) : null}
          {badge ? (
            <span className="agent-badge" style={badgeStyle}>{badge}</span>
          ) : null}
          {person?.teamName ? (
            <span style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
              {person.teamName}
            </span>
          ) : null}
        </div>
        <div style={{ alignItems: "center", display: "flex", gap: "0.5rem" }}>
          {person?.scopeDisplayName ? (
            <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
              {person.scopeDisplayName}
            </span>
          ) : null}
          {person ? (
            <button
              type="button"
              onClick={() => (isEditing ? cancelEdit() : startEdit(person))}
              style={{
                background: isEditing ? "var(--danger)" : "var(--surface-sunken)",
                border: "1px solid var(--border)",
                borderRadius: "6px",
                color: isEditing ? "#fff" : "var(--text-secondary)",
                cursor: "pointer",
                fontSize: "12px",
                padding: "2px 10px",
              }}
            >
              {isEditing ? "Cancel" : "Edit"}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return <section className="report-state-card">Loading hierarchy...</section>;
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {error ? <div className="message-banner error">{error}</div> : null}
      {notice ? <div className="message-banner success">{notice}</div> : null}

      <div
        style={{
          display: "grid",
          gap: "1rem",
          gridTemplateColumns: editingId ? "1fr 320px" : "1fr",
        }}
      >
        <div className="settings-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              alignItems: "center",
              borderBottom: "1px solid var(--border)",
              display: "flex",
              gap: "0.75rem",
              justifyContent: "space-between",
              padding: "1rem",
            }}
          >
            <div>
              <h3 style={{ margin: 0 }}>Organization Tree</h3>
              <p style={{ color: "var(--text-secondary)", fontSize: "13px", margin: "0.25rem 0 0" }}>
                Region &rarr; City &rarr; Manager &rarr; Agent. Click Edit to change placement.
              </p>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="button" className="secondary-ghost" onClick={expandAll}>
                Expand all
              </button>
              <button type="button" className="secondary-ghost" onClick={collapseAll}>
                Collapse
              </button>
              <button type="button" className="secondary-ghost" onClick={loadHierarchy}>
                Refresh
              </button>
            </div>
          </div>

          {!hierarchyRows.length ? (
            <div className="report-state-card">No hierarchy data available yet.</div>
          ) : (
            <div>
              {hierarchyRows.map((region) => {
                const rKey = `r-${region.id}`;
                const cityCount = region.cities.length + region.seniorManagers.length;
                return (
                  <React.Fragment key={rKey}>
                    {renderTreeRow(region.name, "Region", { background: "#f0e6ff", border: "1px solid #d4b8ff", color: "#7c3aed" }, 0, rKey, cityCount)}
                    {expanded[rKey] ? (
                      <>
                        {region.seniorManagers.map((sm) =>
                          renderTreeRow(sm.fullName, roleLabel(sm.role), roleBadgeStyle(sm.role), 1, `sm-${sm.id}`, 0, sm)
                        )}
                        {region.cities.map((city) => {
                          const cKey = `c-${city.id}`;
                          const mgrCount = city.managers.length + city.directAgents.length;
                          return (
                            <React.Fragment key={cKey}>
                              {renderTreeRow(city.name, "City", { background: "#e0f2fe", border: "1px solid #7dd3fc", color: "#0369a1" }, 1, cKey, mgrCount)}
                              {expanded[cKey] ? (
                                <>
                                  {city.managers.map((mgr) => {
                                    const mKey = `m-${mgr.id}`;
                                    return (
                                      <React.Fragment key={mKey}>
                                        {renderTreeRow(mgr.fullName, roleLabel(mgr.role), roleBadgeStyle(mgr.role), 2, mKey, mgr.agents.length, mgr)}
                                        {expanded[mKey]
                                          ? mgr.agents.map((agent) =>
                                              renderTreeRow(agent.fullName, roleLabel(agent.role), roleBadgeStyle(agent.role), 3, `a-${agent.id}`, 0, agent)
                                            )
                                          : null}
                                      </React.Fragment>
                                    );
                                  })}
                                  {city.directAgents.map((agent) =>
                                    renderTreeRow(agent.fullName, roleLabel(agent.role), roleBadgeStyle(agent.role), 2, `da-${agent.id}`, 0, agent)
                                  )}
                                </>
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {editingId ? (
          <div className="settings-card" style={{ alignSelf: "start", position: "sticky", top: "1rem" }}>
            <h3 style={{ marginBottom: "0.25rem" }}>Edit Placement</h3>
            <p style={{ color: "var(--text-secondary)", fontSize: "13px", margin: "0 0 1rem" }}>
              {hierarchyUsers.find((u) => String(u.id) === editingId)?.full_name || "User"}
            </p>

            <div style={{ display: "grid", gap: "1rem" }}>
              <label className="field-stack">
                <span>Role</span>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm((p) => ({ ...p, role: e.target.value }))}
                >
                  {ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="field-stack">
                <span>Team</span>
                <select
                  value={editForm.team}
                  onChange={(e) => setEditForm((p) => ({ ...p, team: e.target.value }))}
                >
                  <option value="">No team</option>
                  {teams.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </select>
              </label>

              <div>
                <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600 }}>Cities</span>
                  <div style={{ display: "flex", gap: "0.4rem" }}>
                    <button type="button" onClick={() => setEditForm((p) => ({ ...p, cityNames: Object.keys(HIERARCHY_CITY_REGION) }))} style={{ background: "none", border: "none", color: "var(--primary)", cursor: "pointer", fontSize: "12px", padding: 0 }}>All</button>
                    <span style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>·</span>
                    <button type="button" onClick={() => setEditForm((p) => ({ ...p, cityNames: [] }))} style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontSize: "12px", padding: 0 }}>None</button>
                  </div>
                </div>
                {Object.entries(
                  Object.keys(HIERARCHY_CITY_REGION).reduce((acc, city) => {
                    const region = HIERARCHY_CITY_REGION[city];
                    if (!acc[region]) acc[region] = [];
                    acc[region].push(city);
                    return acc;
                  }, {})
                ).map(([region, cities]) => (
                  <div key={region} style={{ marginBottom: "0.75rem" }}>
                    <div style={{ color: "var(--text-tertiary)", fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em", marginBottom: "0.35rem", textTransform: "uppercase" }}>{region}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                      {cities.map((city) => {
                        const checked = (editForm.cityNames || []).includes(city);
                        return (
                          <button
                            key={city}
                            type="button"
                            onClick={() => setEditForm((p) => ({
                              ...p,
                              cityNames: checked
                                ? (p.cityNames || []).filter((c) => c !== city)
                                : [...(p.cityNames || []), city],
                            }))}
                            style={{
                              background: checked ? "var(--primary)" : "var(--surface-sunken)",
                              border: `1px solid ${checked ? "var(--primary)" : "var(--border)"}`,
                              borderRadius: "20px",
                              color: checked ? "#fff" : "var(--text-secondary)",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: checked ? 600 : 400,
                              padding: "0.3rem 0.75rem",
                              transition: "all 0.15s",
                            }}
                          >
                            {city}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                <button type="button" className="secondary-ghost" onClick={cancelEdit}>
                  Cancel
                </button>
                <button type="button" className="primary-button" disabled={saving} onClick={handleSaveUser}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="settings-card">
        <h3 style={{ marginBottom: "0.25rem" }}>Team Managers</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "13px", margin: "0 0 1rem" }}>
          Assign or change which manager leads each team.
        </p>
        <div style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
          {teams.map((team) => (
            <div
              key={team.id}
              style={{
                background: "var(--surface-sunken)",
                border: "1px solid var(--border)",
                borderRadius: "10px",
                display: "grid",
                gap: "0.5rem",
                padding: "0.75rem 1rem",
              }}
            >
              <strong style={{ fontSize: "14px" }}>{team.name}</strong>
              <select
                value={team.manager ? String(team.manager) : ""}
                onChange={(e) => handleSaveTeamManager(team.id, e.target.value)}
                disabled={saving}
                style={{ fontSize: "13px" }}
              >
                <option value="">No manager</option>
                {editableManagers.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.full_name} ({roleLabel(u.role)})
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HierarchyTab;
